import { useQuery, useMutation, useQueryClient  } from "@tanstack/react-query";
import { User, PlusCircle } from 'lucide-react';
import { useState } from 'react';
import ParentModal from './ParentModal';
import Portal from '../../../../components/common/Portal';

const ParentsSection = () => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();

	const [parents, setParents] = useState([]);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedParent, setSelectedParent] = useState();

	const { data } = useQuery({
		queryKey: ['member'],
		queryFn: async () => {
			const response = await fetch(`${API_URL}/bbs/ajax.get_member.php`);
			return response.json();
		}
	});
	const member = data?.data ?? null;

	const deleteMutation = useMutation({
		mutationFn: async (id: string) => {
			const params = new URLSearchParams();
			params.append("pr_id", id);

			const response = await fetch(`${API_URL}/bbs/ajax.delete_parent.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						alert("필수 필드값이 입력되지 않았습니다.");
						break;
					default :
						alert("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(!response.data) {
				alert("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data.memberId) {
				alert("삭제되었습니다.");
				queryClient.invalidateQueries({queryKey: ['member']});
			}
		},
		onError: (error: any) => {
			console.log(error.message);
		}
	});

	const handleParentDelete = (id) => {
		if(!confirm("정말로 삭제하시겠습니까?\n삭제한 데이터는 복구할 수 없습니다.")) {
			return;
		}

		if(parents.length <= 1) {
			alert("최소 한 명의 보호자 정보는 입력해야 합니다.");
			return;
		}

		deleteMutation.mutate(id);
	};

	const handleParentModal = (id?: string) => {
		setSelectedParent(id);
		setIsModalOpen(true);
	};

	useEffect(() => {
		if (member?.parents) {
			setParents(member.parents);
		}
	}, [member]);

	return (
		<section className="space-y-3">
			<div className="flex flex-wrap gap-2 items-baseline">
				<h2 className="text-m text-gray-500 font-bold tracking-tighter">부모 / 보호자</h2>
				<span className="text-xs text-slate-400 font-medium">부모 / 보호자는 최대 2명까지 등록 가능합니다.</span>
			</div>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{parents?.map((parent, index) => (
					<div key={index} className="space-y-4">
						<div 
							onClick={() => handleParentModal(parent.id)}
							className="relative p-5 border rounded-xl bg-gray-50 flex flex-col items-end cursor-pointer hover:border-blue-400 transition-colors group "
						>
							<div className="flex space-x-3 mb-1">
								<button 
									className="text-xs text-blue-500 hover:text-blue-700 font-medium"
									onClick={(e) => {
										e.stopPropagation();
										handleParentModal(parent.id);
									}}
								>
									수정
								</button>
								<button 
									className="text-xs text-red-500 hover:text-red-700 font-medium"
									onClick={(e) => {
										e.stopPropagation();
										handleParentDelete(parent.id);
									}}
								>
									삭제
								</button>
							</div>
							<div className="flex items-center w-full text-left">
								<div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm mr-3">
									<User className="w-6 h-6 text-gray-400" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-[15px] font-bold text-gray-900 truncate">
										{parent.name || "이름 없음"}
									</p>
									<div className="mt-1 space-y-0.5">
										<p className="text-xs text-gray-500 flex items-center">
											<span className="w-12 text-gray-400 uppercase tracking-tighter flex-shrink-0">이메일</span>
											<span className="truncate">{parent.email || "-"}</span>
										</p>
										<p className="text-xs text-gray-500 flex items-center">
											<span className="w-12 text-gray-400 tracking-tighter flex-shrink-0">연락처</span>
											<span>{parent.phone || "-"}</span>
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				))}
				{parents.length < 2 && (
					<button
						type="button"
						onClick={() => handleParentModal(null)}
						className="h-full border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-slate-400 hover:bg-slate-50 hover:border-slate-300 transition-all group"
					>
						<div className="p-3 rounded-full  transition-transform">
							<PlusCircle className="w-8 h-8" />
						</div>
					</button>
				)}
				<Portal>
					{isModalOpen && (
						<ParentModal
							id={selectedParent}
							onClose={() => setIsModalOpen(false)} 
						/>
					)}
				</Portal>
			</div>
		</section>
	);
}

export default ParentsSection;